import { Link, useLocation } from "react-router-dom";
import { styled } from "@mui/material/styles";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import type { ListItemButtonProps } from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import MenuList from "@mui/material/MenuList";
import LogoutIcon from "@mui/icons-material/Logout";
import type { OverridableComponent } from "@mui/material/OverridableComponent";
import type { SvgIconTypeMap } from "@mui/material";
import { useAuth } from "../../contexts/Auth";
import menuItems from "./menuItems";

type StyledListItemButtonProps = ListItemButtonProps & { selected?: boolean };

const StyledListItemButton = styled<
  (props: StyledListItemButtonProps) => JSX.Element
>(ListItemButton, {
  shouldForwardProp: (prop) => prop !== "selected",
})(({ theme, selected }) => ({
  display: "flex",
  flexFlow: "row nowrap",
  gap: theme.spacing(0, 2),
  justifyContent: "start",
  alignItems: "center",
  padding: theme.spacing(2),
  color: selected ? theme.palette.primary.main : "inherit",
  fontWeight: selected ? "bold" : "normal",
  ["&:hover"]: {
    backgroundColor: "f0f0f0",
  },
}));

const StyledListItemIcon = styled(ListItemIcon)({
  color: "inherit",
  minWidth: "auto",
});

const StyledTypography = styled(ListItemIcon)(({ theme }) => ({
  color: "inherit",
  fontSize: "1.125rem",
  fontWeight: "inherit",
}));

type MenuItemProps = {
  label: string;
  to?: string;
  Icon: OverridableComponent<SvgIconTypeMap>;
  onClick?: () => void;
  selected?: boolean;
};

function MenuItemLink({ label, Icon, onClick, to, selected }: MenuItemProps) {
  const item = (
    <ListItem disablePadding>
      <StyledListItemButton selected={selected} onClick={onClick}>
        <StyledListItemIcon>
          <Icon height={24} width={24} />
        </StyledListItemIcon>
        <StyledTypography>{label}</StyledTypography>
      </StyledListItemButton>
    </ListItem>
  );

  return to ? <Link to={to}>{item}</Link> : item;
}

export type MenuItems = Pick<MenuItemProps, "label" | "Icon" | "to">[];

export default function DrawerMenu() {
  const { cliente, logout } = useAuth();
  const { pathname } = useLocation();

  const items = menuItems(cliente).filter(({ hidden }) => !hidden);

  return (
    <MenuList disablePadding>
      {items.map(({ label, to, Icon }) => (
        <MenuItemLink
          Icon={Icon}
          label={label}
          to={to}
          key={to}
          selected={pathname === to}
        />
      ))}

      <MenuItemLink
        Icon={LogoutIcon}
        label="Sair"
        key={items.length}
        onClick={logout}
      />
    </MenuList>
  );
}
